import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EconomyPanel } from './components/EconomyPanel';
import { HelpDialog } from './components/HelpDialog';
import { Inspector } from './components/Inspector';
import { LayersPanel } from './components/LayersPanel';
import { LootPanel } from './components/LootPanel';
import { MapCanvas, type View } from './components/MapCanvas';
import { Toolbar, ViewOptions } from './components/Toolbar';
import { downloadBlob, downloadText } from './lib/download';
import { buildModel, itemsFromRows, type LootData } from './lib/loot';
import { DEFAULT_IZURVIVE_VERSION, MAP_PRESETS, presetById, tileSources } from './lib/maps';
import { KIND_META, readEconomyRows } from './lib/model';
import {
  deleteMapImage,
  loadMapImage,
  saveMapImage,
  saveProject,
  saveSettings,
  clearProject,
} from './lib/storage';
import { fetchLootData, fetchVanillaFiles, loadImage, VANILLA_FILES, vanillaImageUrl } from './lib/vanilla';
import { useLayout } from './lib/useLayout';
import { createZip } from './lib/zip';
import { serializeDoc, useDispatch, useFeatures, useStore, type Tool } from './state/store';

const TOOL_KEYS: Record<string, Tool> = {
  Digit1: 'select',
  Digit2: 'point',
  Digit3: 'zone',
  Digit4: 'scatter',
  Digit5: 'erase',
  Digit6: 'measure',
};

/** Bottom sheets of the mobile layout. */
type Sheet = 'layers' | 'loot' | 'inspect' | 'tables' | 'menu';

export function App() {
  const state = useStore();
  const dispatch = useDispatch();
  const { features } = useFeatures();
  const [view, setView] = useState<View>({ cx: 7680, cz: 7680, scale: 0.045 });
  const [showHelp, setShowHelp] = useState(false);
  const [activeTab, setActiveTab] = useState<'map' | string>('map');
  const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const preset = presetById(state.mapId);
  const [loot, setLoot] = useState<LootData | null>(null);
  const [officialImage, setOfficialImage] = useState<HTMLImageElement | null>(null);
  const [tierImage, setTierImage] = useState<HTMLImageElement | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null);
  const [highlightName, setHighlightName] = useState<string | null>(null);
  const { mobile, mode: layoutMode, setMode: setLayoutMode } = useLayout();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [sheetSize, setSheetSize] = useState<'half' | 'full'>('half');

  const typesDoc = useMemo(() => state.docs.find((doc) => doc.kind === 'types') ?? null, [state.docs]);
  const lootModel = useMemo(() => {
    if (!loot || !typesDoc) return null;
    return buildModel(loot, itemsFromRows(loot, readEconomyRows(typesDoc)));
  }, [loot, typesDoc]);
  const highlightItem = (highlightName && lootModel?.byName.get(highlightName)) || null;

  const tableDocs = useMemo(
    () => state.docs.filter((doc) => !KIND_META[doc.kind].mapEditable),
    [state.docs],
  );

  const fit = useCallback(() => {
    const rect = mainRef.current?.getBoundingClientRect();
    const width = rect?.width ?? 900;
    const height = rect?.height ?? 700;
    const scale = (Math.min(width, height) * 0.92) / state.mapSize;
    setView({ cx: state.mapSize / 2, cz: state.mapSize / 2, scale });
  }, [state.mapSize]);

  useEffect(() => {
    fit();
    // Re-fit when the terrain size changes.
  }, [fit]);

  /** Frames every position of a group, with a little breathing room. */
  const zoomToGroup = useCallback(
    (groupId: string) => {
      const members = features.filter((feature) => feature.groupId === groupId);
      if (members.length === 0) return;
      const pad = 200;
      const xs = members.flatMap((m) => [m.x - (m.r ?? 0) - pad, m.x + (m.r ?? 0) + pad]);
      const zs = members.flatMap((m) => [m.z - (m.r ?? 0) - pad, m.z + (m.r ?? 0) + pad]);
      const width = Math.max(...xs) - Math.min(...xs);
      const height = Math.max(...zs) - Math.min(...zs);
      const rect = mainRef.current?.getBoundingClientRect();
      const scale = Math.min((rect?.width ?? 900) / width, (rect?.height ?? 700) / height);
      setActiveTab('map');
      setView({
        cx: (Math.max(...xs) + Math.min(...xs)) / 2,
        cz: (Math.max(...zs) + Math.min(...zs)) / 2,
        scale: Math.min(scale, 1),
      });
    },
    [features],
  );

  // Notices fade on their own so they never cover the map.
  useEffect(() => {
    if (!state.notice) return;
    const timer = setTimeout(() => dispatch({ type: 'dismissNotice' }), 5000);
    return () => clearTimeout(timer);
  }, [dispatch, state.notice]);

  /* ---------------- vanilla data ---------------- */
  // Bundled official map, tier zones and loot buildings for terrains we ship data for.
  useEffect(() => {
    let cancelled = false;
    setLoot(null);
    setOfficialImage(null);
    setTierImage(null);
    setSelectedBuilding(null);
    const folder = preset.vanilla;
    if (!folder) return;
    loadImage(vanillaImageUrl(folder, 'map.jpg'))
      .then((image) => !cancelled && setOfficialImage(image))
      .catch(() => undefined);
    loadImage(vanillaImageUrl(folder, 'tiers.png'))
      .then((image) => !cancelled && setTierImage(image))
      .catch(() => undefined);
    fetchLootData(folder)
      .then((data) => !cancelled && setLoot(data))
      .catch((error: Error) => dispatch({ type: 'notice', kind: 'error', text: `Loot data: ${error.message}` }));
    return () => {
      cancelled = true;
    };
  }, [dispatch, preset.vanilla]);

  const loadVanilla = useCallback(
    async (fileNames?: string[], replace = false) => {
      if (!preset.vanilla) return;
      const wanted = VANILLA_FILES.filter((f) => (fileNames ? fileNames.includes(f.fileName) : f.loadByDefault));
      try {
        const files = await fetchVanillaFiles(preset.vanilla, wanted);
        dispatch({ type: 'loadFiles', files, replace });
      } catch (error) {
        dispatch({ type: 'notice', kind: 'error', text: `Could not load vanilla files: ${(error as Error).message}` });
      }
    },
    [dispatch, preset.vanilla],
  );

  // First visit: start from Bohemia's vanilla files so there is nothing to upload.
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    if (state.docs.length === 0) void loadVanilla(undefined, true);
  }, [loadVanilla, state.docs.length]);

  const zoomToBuilding = useCallback(
    (index: number) => {
      const building = loot?.buildings[index];
      if (!building) return;
      setActiveTab('map');
      const scale = 4;
      // On phones a sheet covers part of the map - the bottom (portrait) or the right side
      // (landscape): centre the building in what is left.
      const sheetRect = mobile ? document.querySelector('.m-sheet')?.getBoundingClientRect() : undefined;
      const side = sheetRect !== undefined && sheetRect.width < window.innerWidth * 0.9;
      const coveredX = sheetRect && side ? sheetRect.width : 0;
      const coveredY = sheetRect && !side ? sheetRect.height : 0;
      setView({ cx: building.x + coveredX / 2 / scale, cz: building.z - coveredY / 2 / scale, scale });
    },
    [loot, mobile],
  );

  // Phones: tapping a building opens its loot (even when it is already selected),
  // tapping a spawn position opens the editor for it.
  const selectBuildingOnMap = useCallback(
    (index: number | null) => {
      setSelectedBuilding(index);
      if (mobile && index !== null) setSheet('loot');
    },
    [mobile],
  );
  const hasSelection = state.selection.length > 0;
  useEffect(() => {
    if (mobile && hasSelection) setSheet((current) => (current === 'loot' || current === null ? 'inspect' : current));
  }, [mobile, hasSelection]);

  /* ---------------- file loading ---------------- */
  const applyMapImage = useCallback(
    async (file: File) => {
      await saveMapImage(state.mapId, file);
      const image = new Image();
      image.onload = () => setMapImage(image);
      image.src = URL.createObjectURL(file);
      dispatch({ type: 'setSettings', patch: { basemap: 'custom' } });
    },
    [dispatch, state.mapId],
  );

  const readFiles = useCallback(
    async (files: File[]) => {
      const xmlFiles = files.filter((file) => file.name.toLowerCase().endsWith('.xml'));
      const imageFile = files.find((file) => file.type.startsWith('image/'));

      if (xmlFiles.length > 0) {
        const payload = await Promise.all(
          xmlFiles.map(async (file) => ({ fileName: file.name, content: await file.text() })),
        );
        dispatch({ type: 'loadFiles', files: payload });
      }
      if (imageFile) await applyMapImage(imageFile);
      if (xmlFiles.length === 0 && !imageFile) {
        dispatch({ type: 'notice', kind: 'error', text: 'Drop .xml mission files or a map image.' });
      }
    },
    [applyMapImage, dispatch],
  );

  // Restore the stored background whenever the terrain changes.
  useEffect(() => {
    let cancelled = false;
    setMapImage(null);
    loadMapImage(state.mapId)
      .then((blob) => {
        if (!blob || cancelled) return;
        const image = new Image();
        image.onload = () => {
          if (!cancelled) setMapImage(image);
        };
        image.src = URL.createObjectURL(blob);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [state.mapId]);

  /* ---------------- autosave ---------------- */
  useEffect(() => {
    if (!state.dirty) return;
    const timer = setTimeout(() => {
      const saved = saveProject({
        files: state.docs.map((doc) => ({
          fileName: doc.fileName,
          content: serializeDoc(doc),
          visible: doc.visible,
          color: doc.color,
          locked: doc.locked,
        })),
        mapId: state.mapId,
        mapSize: state.mapSize,
        savedAt: Date.now(),
      });
      if (!saved) {
        dispatch({
          type: 'notice',
          kind: 'error',
          text: 'Your browser refused to autosave (storage full). Export your files so edits are not lost.',
        });
      }
      dispatch({ type: 'markSaved' });
    }, 700);
    return () => clearTimeout(timer);
  }, [dispatch, state.dirty, state.docs, state.mapId, state.mapSize]);

  useEffect(() => {
    saveSettings(state.settings);
  }, [state.settings]);

  /* ---------------- keyboard ---------------- */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        dispatch({ type: 'redo' });
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (state.selection.length > 0) {
          event.preventDefault();
          dispatch({ type: 'deleteSelection' });
        }
        return;
      }
      if (event.key === 'Escape') {
        dispatch({ type: 'setSelection', ids: [] });
        return;
      }
      if (event.key.toLowerCase() === 'f') {
        fit();
        return;
      }
      const tool = TOOL_KEYS[event.code];
      if (tool) dispatch({ type: 'setTool', tool });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch, fit, state.selection.length]);

  /* ---------------- export ---------------- */
  const exportAll = () => {
    if (state.docs.length === 0) return;
    if (state.docs.length === 1) {
      downloadText(serializeDoc(state.docs[0]), state.docs[0].fileName);
      return;
    }
    const zip = createZip(state.docs.map((doc) => ({ name: doc.fileName, content: serializeDoc(doc) })));
    downloadBlob(zip, 'dayz-mission-files.zip');
  };

  const activeDoc = state.docs.find((doc) => doc.id === activeTab);

  const sources = useMemo(
    () => tileSources(state.settings.izurviveVersion).filter((source) => source.mapId === state.mapId),
    [state.mapId, state.settings.izurviveVersion],
  );
  const basemap = state.settings.basemap;
  const tileSource = sources.find((source) => source.id === basemap) ?? null;
  const background =
    basemap === 'none' ? null : basemap === 'custom' ? mapImage : (officialImage ?? mapImage);
  const loadedNames = new Set(state.docs.map((doc) => doc.fileName));

  /* ---------------- shared pieces (desktop and mobile layouts) ---------------- */
  const terrainFields = (
    <>
      <label className="field">
        Terrain
        <select value={state.mapId} onChange={(event) => dispatch({ type: 'setMap', mapId: event.target.value })}>
          {MAP_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field" title="Terrain size in metres. Presets are a starting point - correct it here if your terrain differs.">
        Size (m)
        <input
          type="number"
          min={1024}
          step={256}
          value={state.mapSize}
          onChange={(event) =>
            dispatch({ type: 'setMap', mapId: state.mapId, mapSize: Number(event.target.value) || state.mapSize })
          }
        />
      </label>
    </>
  );

  const fileActions = (
    <>
      <button type="button" onClick={() => fileInput.current?.click()}>
        Open XML…
      </button>
      <button type="button" onClick={() => imageInput.current?.click()}>
        Map image…
      </button>
      {mapImage && (
        <button
          type="button"
          title="Remove the stored map image"
          onClick={() => {
            void deleteMapImage(state.mapId);
            setMapImage(null);
          }}
        >
          ✕ image
        </button>
      )}
      {preset.vanilla && (
        <>
          <button
            type="button"
            title="Replace the open files with Bohemia's vanilla mission files"
            onClick={() => {
              if (state.docs.length > 0 && !confirm('Replace the open files with the vanilla files?')) return;
              void loadVanilla(undefined, true);
            }}
          >
            Load vanilla
          </button>
          <select
            value=""
            title="Open another vanilla file"
            onChange={(event) => {
              if (event.target.value) void loadVanilla([event.target.value]);
            }}
          >
            <option value="">+ vanilla file…</option>
            {VANILLA_FILES.filter((f) => !loadedNames.has(f.fileName)).map((f) => (
              <option key={f.fileName} value={f.fileName}>
                {f.label} ({f.fileName})
              </option>
            ))}
          </select>
        </>
      )}
    </>
  );

  const exportButton = (
    <button type="button" className="primary" onClick={exportAll} disabled={state.docs.length === 0}>
      Export{state.docs.length > 1 ? ' all' : ''}
    </button>
  );

  const resetButton = (
    <button
      type="button"
      onClick={() => {
        if (state.docs.length > 0 && !confirm('Discard all loaded files?')) return;
        clearProject();
        dispatch({ type: 'clearAll' });
      }}
    >
      Reset
    </button>
  );

  const mapOptions = (
    <>
      <label className="check" title="Map background">
        Map
        <select
          value={basemap}
          onChange={(event) => dispatch({ type: 'setSettings', patch: { basemap: event.target.value } })}
        >
          {preset.vanilla && <option value="official">Bohemia satellite (bundled)</option>}
          {sources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.label} (online)
            </option>
          ))}
          <option value="custom">My image{mapImage ? '' : ' (none loaded)'}</option>
          <option value="none">None</option>
        </select>
      </label>
      {basemap.startsWith('izurvive') && (
        <label className="check" title="iZurvive map version in its tile URLs, e.g. 1.25.0">
          v
          <input
            className="version-input"
            value={state.settings.izurviveVersion}
            placeholder={DEFAULT_IZURVIVE_VERSION}
            onChange={(event) => dispatch({ type: 'setSettings', patch: { izurviveVersion: event.target.value } })}
          />
        </label>
      )}
      {loot && (
        <>
          <label className="check" title="Loot buildings, coloured by tier">
            <input
              type="checkbox"
              checked={state.settings.showBuildings}
              onChange={(event) => dispatch({ type: 'setSettings', patch: { showBuildings: event.target.checked } })}
            />
            Buildings
          </label>
          <label className="check" title="Loot tier zones from areaflags.map">
            <input
              type="checkbox"
              checked={state.settings.showTiers}
              onChange={(event) => dispatch({ type: 'setSettings', patch: { showTiers: event.target.checked } })}
            />
            Tiers
          </label>
        </>
      )}
    </>
  );

  const zoomBy = (factor: number) => setView((v) => ({ ...v, scale: Math.min(Math.max(v.scale * factor, 0.004), 24) }));

  const mapCanvas = (
    <MapCanvas
      view={view}
      onViewChange={setView}
      mapImage={background}
      tileSource={tileSource}
      tierImage={state.settings.showTiers ? tierImage : null}
      loot={loot}
      lootModel={lootModel}
      showBuildings={state.settings.showBuildings}
      selectedBuilding={selectedBuilding}
      onSelectBuilding={selectBuildingOnMap}
      highlightItem={highlightItem}
    />
  );

  const lootPanel =
    loot && lootModel && typesDoc ? (
      <LootPanel
        model={lootModel}
        typesDoc={typesDoc}
        building={selectedBuilding !== null ? loot.buildings[selectedBuilding] : null}
        mapSize={state.mapSize}
        highlightItem={highlightName}
        onHighlight={setHighlightName}
        onSelectBuilding={setSelectedBuilding}
        onZoomToBuilding={zoomToBuilding}
      />
    ) : loot ? (
      <div className="panel">
        <p className="empty">Open types.xml (Load vanilla) to see what spawns in each building.</p>
      </div>
    ) : null;

  const attribution = (
    <>
      {tileSource ? `${tileSource.attribution} · ` : ''}
      {preset.vanilla ? 'Map & vanilla data © Bohemia Interactive (ADPL-SA) · ' : ''}
    </>
  );

  const overlays = (
    <>
      {state.notice && (
        <div className={`toast ${state.notice.kind}`} onClick={() => dispatch({ type: 'dismissNotice' })}>
          {state.notice.text}
          <span className="muted"> ({mobile ? 'tap' : 'click'} to dismiss)</span>
        </div>
      )}

      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}

      <input
        ref={fileInput}
        type="file"
        accept=".xml,text/xml,application/xml"
        multiple
        hidden
        onChange={(event) => {
          void readFiles([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
      <input
        ref={imageInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void applyMapImage(file);
          event.target.value = '';
        }}
      />
    </>
  );

  if (mobile) {
    const sheetTitle: Record<Sheet, string> = {
      layers: 'Layers',
      loot: 'Loot',
      inspect: 'Inspector',
      tables: 'Tables',
      menu: 'Menu',
    };
    const onMap = activeTab === 'map' || !activeDoc;
    const tab = (id: Sheet | 'map', label: string, icon: string, badge?: number) => (
      <button
        type="button"
        className={(id === 'map' ? onMap && sheet === null : sheet === id) ? 'active' : ''}
        onClick={() => {
          if (id === 'map') {
            setSheet(null);
            setActiveTab('map');
          } else {
            setSheet(sheet === id ? null : id);
          }
        }}
      >
        <span className="m-tab-icon" aria-hidden>
          {icon}
        </span>
        {label}
        {badge ? <span className="m-badge">{badge}</span> : null}
      </button>
    );

    return (
      <div className={`app mobile ${sheet ? `sheet-${sheetSize}` : ''}`}>
        <header className="m-header">
          <span className="logo">◆</span>
          <h1>DayZ Editor</h1>
          <span className="m-status muted">{state.dirty ? 'saving…' : 'saved'}</span>
          {exportButton}
        </header>

        <main className="main m-main" ref={mainRef}>
          {onMap ? (
            <>
              {mapCanvas}
              <div className="m-tools">
                <Toolbar compact />
              </div>
              <div className="m-zoom">
                <button type="button" aria-label="Zoom in" onClick={() => zoomBy(1.5)}>
                  +
                </button>
                <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.5)}>
                  −
                </button>
                <button type="button" aria-label="Fit map" onClick={fit}>
                  ⤢
                </button>
              </div>
            </>
          ) : (
            <EconomyPanel doc={activeDoc} />
          )}
        </main>

        {sheet && (
          <section className="m-sheet" aria-label={sheetTitle[sheet]}>
            <div className="m-sheet-head">
              <button
                type="button"
                className="m-grip"
                aria-label={sheetSize === 'full' ? 'Shrink panel' : 'Expand panel'}
                onClick={() => setSheetSize(sheetSize === 'full' ? 'half' : 'full')}
              />
              <b>{sheetTitle[sheet]}</b>
              <button
                type="button"
                className="icon"
                aria-label={sheetSize === 'full' ? 'Shrink panel' : 'Expand panel'}
                onClick={() => setSheetSize(sheetSize === 'full' ? 'half' : 'full')}
              >
                {sheetSize === 'full' ? '▾' : '▴'}
              </button>
              <button type="button" className="icon" aria-label="Close panel" onClick={() => setSheet(null)}>
                ✕
              </button>
            </div>
            <div className="m-sheet-body">
              {sheet === 'layers' && (
                <LayersPanel
                  onZoomToGroup={(groupId) => {
                    zoomToGroup(groupId);
                    setSheet(null);
                  }}
                  onOpenTable={(docId) => {
                    setActiveTab(docId);
                    setSheet(null);
                  }}
                />
              )}
              {sheet === 'loot' &&
                (lootPanel ?? (
                  <div className="panel">
                    <p className="empty">Loot data is only bundled for Chernarus+.</p>
                  </div>
                ))}
              {sheet === 'inspect' && <Inspector />}
              {sheet === 'tables' && (
                <div className="panel">
                  <p className="empty">Loot and event economy tables. Open one to edit it full screen.</p>
                  <div className="m-list">
                    {tableDocs.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        className={activeTab === doc.id ? 'active' : ''}
                        onClick={() => {
                          setActiveTab(doc.id);
                          setSheet(null);
                        }}
                      >
                        {doc.fileName}
                        <span className="muted">{KIND_META[doc.kind].label}</span>
                      </button>
                    ))}
                    {tableDocs.length === 0 && <p className="empty">No types.xml or events.xml loaded.</p>}
                  </div>
                </div>
              )}
              {sheet === 'menu' && (
                <div className="panel m-menu">
                  <h2>Map</h2>
                  <div className="m-menu-grid">{mapOptions}</div>
                  <div className="m-menu-grid">
                    <ViewOptions />
                  </div>
                  <h2>Files</h2>
                  <div className="m-menu-grid">
                    {terrainFields}
                    {fileActions}
                    {resetButton}
                  </div>
                  <h2>More</h2>
                  <div className="m-menu-grid">
                    <button type="button" onClick={() => setShowHelp(true)}>
                      Help
                    </button>
                    <button type="button" onClick={() => setLayoutMode('desktop')}>
                      Desktop layout
                    </button>
                  </div>
                  <p className="hint">
                    {state.docs.length} file(s) · {preset.name} {state.mapSize} m · {attribution}files never leave
                    your device
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        <nav className="m-tabbar">
          {tab('map', 'Map', '◈')}
          {tab('layers', 'Layers', '☰')}
          {tab('loot', 'Loot', '⌂')}
          {tab('inspect', 'Edit', '✎', state.selection.length)}
          {tab('tables', 'Tables', '▤')}
          {tab('menu', 'More', '⋯')}
        </nav>

        {overlays}
      </div>
    );
  }

  return (
    <div
      className={`app ${dragOver ? 'drag-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        void readFiles([...event.dataTransfer.files]);
      }}
    >
      <header className="header">
        <div className="brand">
          <span className="logo">◆</span>
          <div>
            <h1>DayZ Console Server Editor</h1>
            <span className="muted">animal territories · event &amp; item spawns · loot economy</span>
          </div>
        </div>

        <div className="header-actions">
          {terrainFields}
          {fileActions}
          {exportButton}
          {resetButton}
          <button type="button" onClick={() => setShowHelp(true)}>
            Help
          </button>
          {layoutMode !== 'auto' && (
            <button type="button" title="Go back to picking the layout from the screen size" onClick={() => setLayoutMode('auto')}>
              Auto layout
            </button>
          )}
        </div>
      </header>

      <Toolbar />

      <div className="body">
        <aside className="side left">
          <LayersPanel onZoomToGroup={zoomToGroup} onOpenTable={(docId) => setActiveTab(docId)} />
        </aside>

        <main className="main" ref={mainRef}>
          <div className="tabs">
            <button
              type="button"
              className={activeTab === 'map' ? 'active' : ''}
              onClick={() => setActiveTab('map')}
            >
              Map
            </button>
            {tableDocs.map((doc) => (
              <button
                key={doc.id}
                type="button"
                className={activeTab === doc.id ? 'active' : ''}
                onClick={() => setActiveTab(doc.id)}
              >
                {doc.fileName}
              </button>
            ))}
            {activeTab === 'map' && (
              <span className="tab-tools">
                {mapOptions}
                <button type="button" onClick={fit} title="Fit the terrain to the window (F)">
                  Fit
                </button>
                <button type="button" onClick={() => zoomBy(1.3)}>
                  +
                </button>
                <button type="button" onClick={() => zoomBy(1 / 1.3)}>
                  −
                </button>
              </span>
            )}
          </div>

          {activeTab === 'map' || !activeDoc ? mapCanvas : <EconomyPanel doc={activeDoc} />}
        </main>

        <aside className="side right">
          {lootPanel}
          <Inspector />
        </aside>
      </div>

      <footer className="status">
        <span>
          {state.docs.length} file(s) · {state.selection.length} selected · {presetById(state.mapId).name}{' '}
          {state.mapSize} m
        </span>
        <span className="muted">
          {attribution}
          {state.dirty ? 'saving…' : 'saved locally'} · files never leave your browser
        </span>
      </footer>

      {overlays}

      {dragOver && <div className="drop-overlay">Drop mission XML files or a map image</div>}
    </div>
  );
}
